'use client'

import { ChevronUp } from 'lucide-react'
import { FC, useState } from 'react'

export type BoxChoicePillProps = {
  /** 選択肢の数。バッジに出す */
  count: number
  /** 動画下端からの距離。シークバーとクレジット帯を避けるために呼び出し側が決める */
  bottomOffset?: number
  /** PCは動画上へ重ね、SPは操作エリア内へ同じ見た目のまま配置する */
  placement?: 'overlay' | 'inline'
  onClick: () => void
}

/**
 * BOX 型の「選択肢を開く」ピル。
 *
 * 選択肢を再生中ずっと重ねておくと、動画に焼き込まれたテロップと出演者クレジットが
 * 最初から最後まで隠れてしまう（動画の情報量が落ちる）。かといって hover でしか出さないと
 * スマホで機能せず、「選べること」自体が視聴者に伝わらない。
 *
 * そこで再生中はこの1行だけを置く。映像はほぼ全部見えたまま、インタラクティブ動画である
 * ことは常に伝わり、押せばいつでも選択肢を開ける。終盤は BoxModal 側が自動で開く。
 */
const BoxChoicePill: FC<BoxChoicePillProps> = ({ count, bottomOffset = 0, placement = 'overlay', onClick }) => {
  const [isHovered, setIsHovered] = useState(false)
  const isOverlay = placement === 'overlay'

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      data-testid="box-choice-pill"
      style={{
        position: isOverlay ? 'absolute' : 'relative',
        bottom: isOverlay ? `${bottomOffset}px` : undefined,
        left: isOverlay ? '50%' : undefined,
        zIndex: 28,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        border: '1px solid rgba(255,255,255,0.5)',
        borderRadius: '999px',
        background: isHovered ? 'rgba(8,18,38,0.82)' : 'rgba(8,18,38,0.62)',
        color: '#fff',
        fontFamily: 'inherit',
        fontSize: '12.5px',
        fontWeight: 750,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        boxShadow: '0 8px 24px -10px rgba(0,0,0,0.8)',
        transform: isOverlay
          ? isHovered
            ? 'translateX(-50%) translateY(-2px)'
            : 'translateX(-50%)'
          : isHovered
            ? 'translateY(-2px)'
            : undefined,
        transition: 'background 180ms ease, transform 180ms ease',
        animation: isOverlay ? 'moovy-box-pill-in 260ms ease-out' : undefined,
      }}
    >
      {isOverlay && (
        <style>{`@keyframes moovy-box-pill-in { from { opacity: 0; transform: translateX(-50%) translateY(6px) } to { opacity: 1 } }`}</style>
      )}
      <span
        style={{
          display: 'inline-grid',
          minWidth: '19px',
          height: '19px',
          padding: '0 5px',
          placeItems: 'center',
          borderRadius: '999px',
          background: 'var(--video-brand-color, var(--video-accent-color, #2196F3))',
          fontSize: '11px',
          fontWeight: 850,
        }}
      >
        {count}
      </span>
      次の動画を選ぶ
      <ChevronUp size={13} style={{ opacity: 0.8 }} />
    </button>
  )
}

export default BoxChoicePill
